import ChatbotTab from '../components/Admin/ChatbotTab';
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Paper,
  Divider,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import AdminUserMenu from '../components/Admin/AdminUserMenu';
import ArticleIcon from '@mui/icons-material/Article';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Sort as SortIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SubdirectoryArrowRight as SubcategoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Work as WorkIcon,
  AccountBalanceWallet as TransactionsIcon,
  CreditCard as CreditCardIcon,
  Language as WebsiteIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  VpnKey as VpnKeyIcon,
  ViewQuilt as ViewQuiltIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppMode } from '../contexts/AppModeContext';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';
import { ensureSupabaseConfigured, initSupabase, supabase } from '../lib/supabase';
import { AuthService } from '../services/authService';
import { CategoryService } from '../services/categoryService';
import { SubcategoryService } from '../services/subcategoryService';
import { Category, Subcategory, User } from '../lib/supabase';
import SettingsModal from '../components/Settings/SettingsModal';
import TransactionsTab from '../components/Admin/TransactionsTab';
import WebsiteSettingsTab from '../components/Admin/WebsiteSettingsTab';
import HomepageSettingsTab from '../components/Admin/HomepageSettingsTab';
import toast from 'react-hot-toast';
import { defaultMegaMenuData } from '../components/Layout/MegaMenu';
import { jobMegaMenuData } from '../components/Layout/jobMegaMenuData';
import { AdminKeyService } from '../services/adminKeyService';
import MailProvidersTab from '../components/Admin/MailProvidersTab';
import PlatformSubscribersTab from '../components/Admin/PlatformSubscribersTab';
import PlatformGroupsTab from '../components/Admin/PlatformGroupsTab';
import PlatformCampaignsTab from '../components/Admin/PlatformCampaignsTab';
import PlatformNotificationsTab from '../components/Admin/PlatformNotificationsTab';
import { UserService } from '../services/userService';
import IntegrationsApiKeysTab from '../components/Admin/IntegrationsApiKeysTab';
import BoostVisibilityTab from '../components/Admin/BoostVisibilityTab';
import PlatformSubscriptionsTab from '../components/Admin/PlatformSubscriptionsTab';
import KundencenterTab from '../components/Admin/KundencenterTab';

// Category interface is now imported from Supabase types

// Mock data
const mockStats = {
  totalUsers: 12543,
  totalProducts: 8947,
  totalOrders: 3456,
  totalRevenue: 125430.50,
  userGrowth: 12.5,
  productGrowth: 8.3,
  orderGrowth: 15.2,
  revenueGrowth: 18.7,
};

const mockRecentOrders = [
  {
    id: 'ORD-001',
    customer: 'John Doe',
    product: 'Premium T-Shirt',
    amount: 29.99,
    status: 'completed',
    date: '2024-01-22',
  },
  {
    id: 'ORD-002',
    customer: 'Jane Smith',
    product: 'Designer Jeans',
    amount: 89.99,
    status: 'pending',
    date: '2024-01-22',
  },
  {
    id: 'ORD-003',
    customer: 'Mike Johnson',
    product: 'Summer Dress',
    amount: 65.99,
    status: 'shipped',
    date: '2024-01-21',
  },
  {
    id: 'ORD-004',
    customer: 'Sarah Wilson',
    product: 'Classic Blazer',
    amount: 149.99,
    status: 'cancelled',
    date: '2024-01-21',
  },
];

const mockUsers = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john@example.com',
    role: 'customer',
    status: 'active',
    joinDate: '2024-01-15',
    orders: 5,
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane@example.com',
    role: 'seller',
    status: 'active',
    joinDate: '2024-01-10',
    orders: 23,
  },
  {
    id: 3,
    name: 'Mike Johnson',
    email: 'mike@example.com',
    role: 'customer',
    status: 'inactive',
    joinDate: '2024-01-05',
    orders: 2,
  },
];

// Categories will be loaded from Supabase

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { logout, user: storeUser } = useAuthStore();
  const { is_job } = useAppMode();

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  
  // Tab persistence key
  const ADMIN_DASHBOARD_TAB_STORAGE_KEY = 'admin_dashboard_active_tab';
  
  // Initialize active tab from localStorage or default to 'dashboard'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem(ADMIN_DASHBOARD_TAB_STORAGE_KEY);
    // map legacy 'mailjet' to new 'mail'
    if (savedTab === 'mailjet') return 'mail';
    return savedTab || 'dashboard';
  });
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const ADMIN_SIDEBAR_COLLAPSED_KEY = 'admin_sidebar_collapsed_v1';
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(ADMIN_SIDEBAR_COLLAPSED_KEY);
    return saved === '1';
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    imageUrl: '',
    imageFile: null as File | null,
    isActive: true,
    isJobCategory: false,
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Keep sidebar always open on desktop. If the screen switches to desktop, force open.
  React.useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((p) => {
      const next = !p;
      localStorage.setItem(ADMIN_SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };
  
  // Subcategory state
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [subcategoryModalOpen, setSubcategoryModalOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    description: '',
    categoryId: '',
    imageUrl: '',
    imageFile: null as File | null,
    isActive: true,
    isJobSubcategory: false,
  });
  const [isUploadingSubcategoryImage, setIsUploadingSubcategoryImage] = useState(false);
  const [showSubcategories, setShowSubcategories] = useState(false);
  
  // Accordion state
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    subcategories: false,
  });

  // Category filter state
  const [categoryFilters, setCategoryFilters] = useState({
    search: '',
    status: 'all', // 'all', 'active', 'inactive'
    sortBy: 'name', // 'name', 'created_at', 'updated_at', 'product_count'
    sortOrder: 'asc', // 'asc', 'desc'
    categoryType: 'all' as 'all' | 'products' | 'jobs',
  });
  const [showCategoryFilters, setShowCategoryFilters] = useState(false);

  // Subcategory filter state
  const [subcategoryFilters, setSubcategoryFilters] = useState({
    search: '',
    category: '',
    status: 'all', // 'all', 'active', 'inactive'
    sortBy: 'name', // 'name', 'created_at', 'updated_at', 'product_count'
    sortOrder: 'asc', // 'asc', 'desc'
    subcategoryType: 'all' as 'all' | 'products' | 'jobs',
  });
  const [showSubcategoryFilters, setShowSubcategoryFilters] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [productCategoriesOpen, setProductCategoriesOpen] = useState(false);
  const [jobCategoriesOpen, setJobCategoriesOpen] = useState(false);
  const [selectedMegaCategory, setSelectedMegaCategory] = useState<string>('Damen');
  const [categoriesEnabled, setCategoriesEnabled] = useState<Record<string, boolean>>({});
  const [settingsMode, setSettingsMode] = useState<'shop' | 'job' | 'med' | 'edu'>('shop');
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);
  const [subcategoriesEnabled, setSubcategoriesEnabled] = useState<Record<string, Record<string, boolean>>>({});
  
  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userToolsQuery, setUserToolsQuery] = useState('');
  const [userToolsBusy, setUserToolsBusy] = useState<null | 'search' | 'find_delete'>(null);
  const [userViewOpen, setUserViewOpen] = useState(false);
  const [userEditOpen, setUserEditOpen] = useState(false);
  const [userDeleteOpen, setUserDeleteOpen] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState<any>(null);
  const [editUserForm, setEditUserForm] = useState<{ alias: string; first_name: string; last_name: string; is_admin: boolean; user_status_preference: string }>({
    alias: '',
    first_name: '',
    last_name: '',
    is_admin: false,
    user_status_preference: 'consumer',
  });

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'users', label: 'Users', icon: <PeopleIcon /> },
    { id: 'kundencenter', label: 'Kundencenter', icon: <SupportAgentIcon /> },
    { id: 'products', label: is_job ? 'Jobs' : 'Products', icon: <InventoryIcon /> },
    { id: 'categories', label: 'Categories', icon: <CategoryIcon /> },
    { id: 'product-categories', label: is_job ? 'Job Categories' : 'Product Categories', icon: <CategoryIcon /> },
    { id: 'transactions', label: 'Transactions', icon: <TransactionsIcon /> },
    { id: 'platform-purchases', label: 'Platform Purchases', icon: <ShoppingCartIcon /> },
    { id: 'page-editor', label: 'Page editor', icon: <EditIcon /> },
    { id: 'post-editor', label: 'Post Editor', icon: <ArticleIcon /> },
    { id: 'media-gallery', label: 'Media Gallery', icon: <PhotoLibraryIcon /> },
    { id: 'website-settings', label: 'Website Settings', icon: <WebsiteIcon /> },
    { id: 'integrations', label: 'Integrations & API Keys', icon: <VpnKeyIcon /> },
    { id: 'chatbot', label: 'Chatbot', icon: <ChatIcon /> },
    { id: 'mail', label: 'Mail', icon: <EmailIcon /> },
    { id: 'homepage', label: 'Seiten', icon: <HomeIcon /> },
    { id: 'footer', label: 'Footer', icon: <ViewQuiltIcon /> },
    { id: 'orders', label: 'Orders', icon: <ShoppingCartIcon /> },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 'notifications', label: 'Notifications', icon: <NotificationsIcon /> },
    { id: 'security', label: 'Security', icon: <SecurityIcon /> },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
  ];

  const MAIL_MENU_OPEN_KEY = 'admin_mail_menu_open_v1';
  const [mailMenuOpen, setMailMenuOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(MAIL_MENU_OPEN_KEY);
    return saved === '1';
  });

  const PLATFORM_PURCHASES_MENU_OPEN_KEY = 'admin_platform_purchases_menu_open_v1';
  const [platformPurchasesMenuOpen, setPlatformPurchasesMenuOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(PLATFORM_PURCHASES_MENU_OPEN_KEY);
    return saved === '1';
  });

  const KUNDENCENTER_MENU_OPEN_KEY = 'admin_kundencenter_menu_open_v1';
  const [kundencenterMenuOpen, setKundencenterMenuOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(KUNDENCENTER_MENU_OPEN_KEY);
    return saved === '1';
  });

  const isMailActive =
    activeTab === 'mail' ||
    activeTab === 'mail-subscribers' ||
    activeTab === 'mail-groups' ||
    activeTab === 'mail-campaigns' ||
    activeTab === 'mail-notifications';

  const isPlatformPurchasesActive =
    activeTab === 'platform-purchases' ||
    activeTab === 'platform-purchases-boost' ||
    activeTab === 'platform-purchases-subscriptions';

  const isKundencenterActive = activeTab === 'kundencenter';

  const toggleMailMenu = () => {
    setMailMenuOpen((p) => {
      const next = !p;
      localStorage.setItem(MAIL_MENU_OPEN_KEY, next ? '1' : '0');
      return next;
    });
  };

  const togglePlatformPurchasesMenu = () => {
    setPlatformPurchasesMenuOpen((p) => {
      const next = !p;
      localStorage.setItem(PLATFORM_PURCHASES_MENU_OPEN_KEY, next ? '1' : '0');
      return next;
    });
  };

  const toggleKundencenterMenu = () => {
    setKundencenterMenuOpen((p) => {
      const next = !p;
      localStorage.setItem(KUNDENCENTER_MENU_OPEN_KEY, next ? '1' : '0');
      return next;
    });
  };

  const openKundencenterReports = () => {
    setActiveTab('kundencenter');
    localStorage.setItem(ADMIN_DASHBOARD_TAB_STORAGE_KEY, 'kundencenter');
    localStorage.setItem('admin_kundencenter_tab', 'reports');
    if (!kundencenterMenuOpen) {
      localStorage.setItem(KUNDENCENTER_MENU_OPEN_KEY, '1');
      setKundencenterMenuOpen(true);
    }
  };

  const openPlatformPurchasesSubTab = (tabId: 'platform-purchases-boost' | 'platform-purchases-subscriptions') => {
    setActiveTab(tabId);
    localStorage.setItem(ADMIN_DASHBOARD_TAB_STORAGE_KEY, tabId);
    if (!platformPurchasesMenuOpen) {
      localStorage.setItem(PLATFORM_PURCHASES_MENU_OPEN_KEY, '1');
      setPlatformPurchasesMenuOpen(true);
    }
  };

  const displayName = currentUser?.firstName || currentUser?.email || 'Admin';
  const avatarLetter = currentUser?.firstName?.charAt(0) || currentUser?.email?.charAt(0) || 'A';

  const openMailSubTab = (tabId: 'mail-subscribers' | 'mail-groups' | 'mail-campaigns' | 'mail-notifications') => {
    setActiveTab(tabId);
    localStorage.setItem(ADMIN_DASHBOARD_TAB_STORAGE_KEY, tabId);
    if (!mailMenuOpen) {
      localStorage.setItem(MAIL_MENU_OPEN_KEY, '1');
      setMailMenuOpen(true);
    }
  };

  const searchUsers = async (rawQuery: string) => {
    const q = (rawQuery || '').trim();
    if (!q) {
      await loadUsers();
      return;
    }

    const withTimeout = async (p: Promise<any>, ms: number, label: string): Promise<any> => {
      let t: any;
      const timeout = new Promise<any>((_, reject) => {
        t = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
      });
      try {
        return await Promise.race([p, timeout]);
      } finally {
        clearTimeout(t);
      }
    };

    setUsersLoading(true);
    try {
      const looksLikeUuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(q);

      const orParts: string[] = [];
      orParts.push(`email.ilike.%${q.replaceAll(',', '\\,')}%`);
      orParts.push(`alias.ilike.%${q.replaceAll(',', '\\,')}%`);
      if (looksLikeUuid) {
        orParts.push(`id.eq.${q.replaceAll(',', '\\,')}`);
      }

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .or(orParts.join(','))
        .order('created_at', { ascending: false });

      if (error) throw error;

      const rows = data || [];
      if (rows.length > 0) {
        setUsers(rows);
        return;
      }

      // Fallback: search Supabase Auth users via admin edge function (for accounts missing from public.users)
      try {
        const invokePromise = supabase.functions.invoke('admin-find-user', { body: { query: q } }) as Promise<any>;
        const res = await withTimeout(invokePromise, 12000, 'Auth lookup');
        const fnData = res?.data;
        const fnErr = res?.error;
        if (fnErr) {
          const msg = (fnErr as any)?.message || 'Auth lookup failed';
          throw new Error(msg);
        }
        const fnUsers = Array.isArray(fnData?.users) ? fnData.users : [];
        // Map to our User shape as best-effort; some fields may be missing
        const mapped = fnUsers.map((u: any) => ({
          id: String(u.id),
          email: String(u.email || ''),
          first_name: u.first_name ?? null,
          last_name: u.last_name ?? null,
          alias: u.alias ?? null,
          is_admin: !!u.is_admin,
          user_status_preference: (u.user_status_preference || 'consumer') as any,
          created_at: u.created_at || u.auth_created_at || null,
          updated_at: u.updated_at || null,
          bio: null,
          location: null,
          website: null,
          social_media_links: null,
          interests: null,
          is_public: false,
          notifications_enabled: true,
          selected_address_id: null,
          alias_last_changed: null,
          company_id: null,
        })) as any;
        setUsers(mapped);
      } catch (e: any) {
        setUsers([]);
        toast.error(e?.message || 'Auth lookup failed');
      }
    } catch (e: any) {
      console.error('User search error:', e);
      toast.error(e?.message || 'Failed to search users');
    } finally {
      setUsersLoading(false);
    }
  };

  const runUserToolsSearch = async () => {
    try {
      setUserToolsBusy('search');
      await searchUsers(userToolsQuery);
    } finally {
      setUserToolsBusy(null);
    }
  };

  const clearUserToolsSearch = async () => {
    setUserToolsQuery('');
    await loadUsers();
  };

  const findAndDeleteByQuery = async () => {
    const q = (userToolsQuery || '').trim();
    if (!q) {
      toast.error('Enter an email, alias, or user id');
      return;
    }

    try {
      setUserToolsBusy('find_delete');
      const looksLikeUuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(q);

      const orParts: string[] = [];
      orParts.push(`email.ilike.%${q.replaceAll(',', '\\,')}%`);
      orParts.push(`alias.ilike.%${q.replaceAll(',', '\\,')}%`);
      if (looksLikeUuid) {
        orParts.push(`id.eq.${q.replaceAll(',', '\\,')}`);
      }

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .or(orParts.join(','))
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      let rows = data || [];
      if (rows.length === 0) {
        // fallback to auth search
        const invokePromise = supabase.functions.invoke('admin-find-user', { body: { query: q } }) as Promise<any>;
        const res = await Promise.race([
          invokePromise,
          new Promise<any>((_, reject) => setTimeout(() => reject(new Error('Auth lookup timed out')), 12000)),
        ]);
        const fnData = res?.data;
        const fnErr = res?.error;
        if (fnErr) {
          const msg = (fnErr as any)?.message || 'Auth lookup failed';
          throw new Error(msg);
        }
        const fnUsers = Array.isArray(fnData?.users) ? fnData.users : [];
        rows = fnUsers.map((u: any) => ({
          id: String(u.id),
          email: String(u.email || ''),
          first_name: u.first_name ?? null,
          last_name: u.last_name ?? null,
          alias: u.alias ?? null,
          is_admin: !!u.is_admin,
          user_status_preference: (u.user_status_preference || 'consumer') as any,
          created_at: u.created_at || u.auth_created_at || null,
          updated_at: u.updated_at || null,
          bio: null,
          location: null,
          website: null,
          social_media_links: null,
          interests: null,
          is_public: false,
          notifications_enabled: true,
          selected_address_id: null,
          alias_last_changed: null,
          company_id: null,
        })) as any;
      }

      setUsers(rows);

      if (rows.length === 0) {
        toast.error('No users found');
        return;
      }
      if (rows.length > 1) {
        toast('Multiple matches found. Refine your search, or delete from the table.', { icon: 'ℹ️' } as any);
        return;
      }

      const u = rows[0] as User;
      if ((u as any).is_admin) {
        toast.error('You cannot delete an admin user');
        return;
      }
      openUserDelete(u);
    } catch (e: any) {
      console.error('Find & delete error:', e);
      toast.error(e?.message || 'Failed to find user');
    } finally {
      setUserToolsBusy(null);
    }
  };

  const handleMenuClick = (itemId: string) => {
    if (itemId === 'logout') {
      handleLogout();
    } else if (itemId === 'settings') {
      // Open settings modal instead of switching to settings tab
      setSettingsOpen(true);
    } else if (itemId === 'page-editor') {
      navigate('/pages-editor');
    } else if (itemId === 'post-editor') {
      navigate('/post-editor');
    } else if (itemId === 'media-gallery') {
      navigate('/admin/media-gallery');
    } else if (itemId === 'footer') {
      navigate('/admin/component-editor');
    } else if (itemId === 'product-categories') {
      if (is_job) {
        setJobCategoriesOpen(true);
      } else {
        setSelectedMegaCategory('Damen');
        setProductCategoriesOpen(true);
      }
    } else {
      setActiveTab(itemId);
      // Save the selected tab to localStorage for persistence
      localStorage.setItem(ADMIN_DASHBOARD_TAB_STORAGE_KEY, itemId);
    }
  };

  // Helpers for subcategory group (left item) enable/disable
  const getSubLabelsFor = React.useCallback((top: string, itemKey: string): string[] => {
    const topData: any = (defaultMegaMenuData as any)[top];
    if (!topData || !topData.rightByKey) return [];
    const cols = topData.rightByKey[itemKey] || [];
    const labels: string[] = [];
    for (const col of cols) {
      for (const it of (col.items || [])) {
        const label = typeof it === 'string' ? it : (it?.label ?? '');
        if (label) labels.push(label);
      }
    }
    return labels;
  }, []);

  const getJobSubLabelsFor = React.useCallback((top: string, itemKey: string): string[] => {
    const topData: any = (jobMegaMenuData as any)[top];
    if (!topData || !topData.rightByKey) return [];
    const cols = topData.rightByKey[itemKey] || topData.rightByKey.all || [];
    const labels: string[] = [];
    for (const col of cols) {
      for (const it of (col.items || [])) {
        const label = typeof it === 'string' ? it : (it?.label ?? '');
        if (label) labels.push(label);
      }
    }
    return labels;
  }, []);

  const isSubcategoryGroupEnabled = React.useCallback((top: string, itemKey: string): boolean => {
    const labels = getSubLabelsFor(top, itemKey);
    if (labels.length === 0) return true;
    return labels.every(l => subcategoriesEnabled?.[top]?.[l] !== false);
  }, [getSubLabelsFor, subcategoriesEnabled]);

  const isJobSubcategoryGroupEnabled = React.useCallback((top: string, itemKey: string): boolean => {
    const labels = getJobSubLabelsFor(top, itemKey);
    if (labels.length === 0) return true;
    return labels.every(l => subcategoriesEnabled?.[top]?.[l] !== false);
  }, [getJobSubLabelsFor, subcategoriesEnabled]);

  const handleToggleSubcategoryGroup = (top: string, itemKey: string, value: boolean) => {
    const labels = getSubLabelsFor(top, itemKey);
    setSubcategoriesEnabled(prev => {
      const nextTop = { ...(prev[top] || {}) } as Record<string, boolean>;
      for (const l of labels) {
        nextTop[l] = value;
      }
      return { ...prev, [top]: nextTop };
    });
  };

  const handleToggleJobSubcategoryGroup = (top: string, itemKey: string, value: boolean) => {
    const labels = getJobSubLabelsFor(top, itemKey);
    setSubcategoriesEnabled(prev => {
      const nextTop = { ...(prev[top] || {}) } as Record<string, boolean>;
      for (const l of labels) {
        nextTop[l] = value;
      }
      return { ...prev, [top]: nextTop };
    });
  };

  // Load website settings on open to initialize category toggles
  React.useEffect(() => {
    const loadWebsiteSettings = async () => {
      try {
        setLoadingSettings(true);
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) throw new Error('Failed to load website settings');
        const data = await res.json();
        const shopTopLevels = Object.keys(defaultMegaMenuData).filter(k => k.toLowerCase() !== 'default');
        const jobTopLevels = Object.keys(jobMegaMenuData).filter(k => k.toLowerCase() !== 'default');
        const topLevels = Array.from(new Set([...shopTopLevels, ...jobTopLevels]));
        const existing: Record<string, boolean> = data?.categoriesEnabled || {};
        const merged: Record<string, boolean> = {};
        for (const k of topLevels) {
          merged[k] = typeof existing[k] === 'boolean' ? existing[k] : true;
        }
        setCategoriesEnabled(merged);

        // Merge subcategoriesEnabled
        const existingSubs: Record<string, Record<string, boolean>> = data?.subcategoriesEnabled || {};
        const mergedSubs: Record<string, Record<string, boolean>> = {};
        for (const top of topLevels) {
          const topData: any = (defaultMegaMenuData as any)[top] || (jobMegaMenuData as any)[top];
          const map: Record<string, boolean> = {};
          if (topData) {
            // Gather all sub labels from rightByKey collections
            const keys = (topData.left || []).map((li: any) => li.key);
            for (const k of keys) {
              const cols = (topData.rightByKey && (topData.rightByKey[k] || topData.rightByKey.all)) || [];
              for (const col of cols) {
                for (const it of (col.items || [])) {
                  const label = typeof it === 'string' ? it : (it?.label ?? '');
                  if (!label) continue;
                  const prev = existingSubs?.[top]?.[label];
                  map[label] = typeof prev === 'boolean' ? prev : true;
                }
              }
            }
          }
          mergedSubs[top] = map;
        }
        setSubcategoriesEnabled(mergedSubs);
        if (data?.mode && ['shop','job','med','edu'].includes(data.mode)) {
          setSettingsMode(data.mode);
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load website settings');
      } finally {
        setLoadingSettings(false);
      }
    };
    if (productCategoriesOpen || jobCategoriesOpen) {
      loadWebsiteSettings();
    }
  }, [productCategoriesOpen, jobCategoriesOpen]);

  const handleToggleCategoryEnabled = (cat: string) => {
    setCategoriesEnabled(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleToggleSubcategoryEnabled = (top: string, label: string) => {
    setSubcategoriesEnabled(prev => ({
      ...prev,
      [top]: { ...(prev[top] || {}), [label]: !prev?.[top]?.[label] }
    }));
  };

  const handleSaveCategoriesEnabled = async () => {
    try {
      setSavingCategories(true);
      // Get admin token
      const secret = await AdminKeyService.getSecret();
      if (!secret) {
        toast.error('Missing admin token to save settings');
        return;
      }
      const payload = {
        mode: settingsMode,
        categoriesEnabled,
        subcategoriesEnabled,
        token: secret,
      } as any;
      const res = await fetch('/api/update-website-settings.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': secret },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'Save failed');
      }
      toast.success('Categories saved');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSavingCategories(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'shipped':
        return 'info';
      case 'cancelled':
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'active':
        return <CheckCircleIcon fontSize="small" />;
      case 'pending':
        return <WarningIcon fontSize="small" />;
      case 'shipped':
        return <VisibilityIcon fontSize="small" />;
      case 'cancelled':
      case 'inactive':
        return <ErrorIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };

  const handleLogout = async () => {
    try {
      await AuthService.signOut();
      logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still logout locally even if Supabase logout fails
      logout();
      navigate('/');
    }
  };

  // Check admin authentication on component mount
  React.useEffect(() => {
    const checkAdminAuth = async () => {
      try {
        // First check auth store (faster and more reliable)
        if (storeUser && (storeUser.isAdmin || storeUser.role === 'admin')) {
          setIsAdmin(true);
          setCurrentUser(storeUser);
          setIsCheckingAuth(false);
          return;
        }

        // Fallback to AuthService if store doesn't have user or admin flag
        const user = await AuthService.getCurrentUser();
        if (user && user.isAdmin) {
          setIsAdmin(true);
          setCurrentUser(user);
          // Update auth store with admin status if it wasn't there
          if (storeUser && !storeUser.isAdmin) {
            useAuthStore.getState().updateUser({ isAdmin: true, role: 'admin' });
          }
        } else {
          console.error('Admin access denied: User is not an admin', { storeUser, dbUser: user });
          navigate('/');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/');
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAdminAuth();
  }, [navigate, storeUser]);

  // Load categories from Supabase
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const categoriesData = await CategoryService.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Load categories when component mounts or when categories tab is active
  React.useEffect(() => {
    if (activeTab === 'categories') {
      loadCategories();
      loadSubcategories();
    }
  }, [activeTab]);

  // Load users when users tab is active
  React.useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab]);

  // Load all users
  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      await initSupabase();
      ensureSupabaseConfigured();

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
        return;
      }

      setUsers(data || []);
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast.error(error?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const openUserView = async (user: User) => {
    setSelectedUser(user);
    setSelectedUserProfile(null);
    setUserViewOpen(true);
    try {
      const profile = await UserService.getUserProfile(String(user.id));
      setSelectedUserProfile(profile);
    } catch {
      setSelectedUserProfile(null);
    }
  };

  const openUserEdit = (user: User) => {
    setSelectedUser(user);
    setEditUserForm({
      alias: String((user as any).alias || ''),
      first_name: String((user as any).first_name || ''),
      last_name: String((user as any).last_name || ''),
      is_admin: Boolean((user as any).is_admin),
      user_status_preference: String((user as any).user_status_preference || 'consumer'),
    });
    setUserEditOpen(true);
  };

  const openUserDelete = (user: User) => {
    setSelectedUser(user);
    setUserDeleteOpen(true);
  };

  const saveUserEdits = async () => {
    if (!selectedUser) return;
    setUserActionLoading(true);
    const toastId = toast.loading('Saving user...');
    try {
      const updates: any = {
        alias: editUserForm.alias.trim() || null,
        first_name: editUserForm.first_name.trim() || null,
        last_name: editUserForm.last_name.trim() || null,
        is_admin: !!editUserForm.is_admin,
        user_status_preference: (editUserForm.user_status_preference || 'consumer').toLowerCase(),
      };

      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', selectedUser.id);

      if (error) throw error;

      toast.success('User updated', { id: toastId });
      setUserEditOpen(false);
      await loadUsers();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update user', { id: toastId });
    } finally {
      setUserActionLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;
    if ((selectedUser as any).is_admin) {
      toast.error('You cannot delete an admin user');
      return;
    }

    setUserActionLoading(true);
    const toastId = toast.loading('Deleting user...');
    try {
      // Prefer server-side full deletion (auth.users + public.users + local JSON)
      // Requires server env SUPABASE_SERVICE_ROLE_KEY and admin token (WEBSITE_SETTINGS_TOKEN)
      let fullDeleteOk = false;
      try {
        const { data, error } = await supabase.functions.invoke('admin-delete-user', {
          body: { id: String(selectedUser.id) },
        });
        if (error) {
          const msg = (error as any)?.message || 'Failed to delete user';
          const details = (error as any)?.context ? JSON.stringify((error as any).context) : '';
          throw new Error(details ? `${msg}: ${details}` : msg);
        }
        if (!data?.ok) {
          throw new Error(data?.error || data?.detail || 'Failed to delete user');
        }
        if (data?.authDeleted === false) {
          throw new Error('Auth user was not deleted (authDeleted=false)');
        }
        fullDeleteOk = true;
      } catch (e: any) {
        // Only fall back when Edge Functions are unavailable/unreachable.
        // If the function returns an error response (forbidden/not configured), do NOT fall back
        // because that would delete only from public.users and leave the auth user behind.
        const msg = (e && (e.message || String(e))) ? String(e.message || e) : '';
        const functionsUnreachable = /Failed to fetch|NetworkError|fetch/i.test(msg);
        if (functionsUnreachable) {
          console.warn('[AdminDashboard] admin-delete-user function unreachable; falling back to users table delete only', e);
        } else {
          throw e;
        }
      }

      if (!fullDeleteOk) {
        const { error } = await supabase
          .from('users')
          .delete()
          .eq('id', selectedUser.id);

        if (error) {
          // Fallback to a soft disable if hard delete is blocked by RLS.
          const soft: any = { is_active: false };
          const { error: softErr } = await supabase.from('users').update(soft).eq('id', selectedUser.id);
          if (softErr) throw error;
        }
      }

      // Best-effort cleanup: delete local storage JSON file
      try {
        const token = await AdminKeyService.getSecret();
        if (token) {
          await fetch('/api/delete-user-file.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ id: String(selectedUser.id), token }),
          });
        }
      } catch {
        // ignore
      }

      toast.success('User removed', { id: toastId });
      setUserDeleteOpen(false);
      setSelectedUser(null);
      setSelectedUserProfile(null);
      await loadUsers();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete user', { id: toastId });
    } finally {
      setUserActionLoading(false);
    }
  };

  // Clear selected category when subcategory type changes
  React.useEffect(() => {
    if (subcategoryForm.categoryId) {
      const selectedCategory = categories.find(cat => cat.id === subcategoryForm.categoryId);
      if (selectedCategory) {
        const isSelectedCategoryJobType = selectedCategory.is_job_category;
        const isSubcategoryJobType = subcategoryForm.isJobSubcategory;
        
        // Clear category if types don't match
        if (isSelectedCategoryJobType !== isSubcategoryJobType) {
          setSubcategoryForm(prev => ({ ...prev, categoryId: '' }));
        }
      }
    }
  }, [subcategoryForm.isJobSubcategory, categories, subcategoryForm.categoryId]);

  // Category management functions
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      description: '',
      imageUrl: '',
      imageFile: null,
      isActive: true,
      isJobCategory: false,
    });
    setCategoryModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      imageUrl: category.image_url || '',
      imageFile: null,
      isActive: category.is_active,
      isJobCategory: category.is_job_category,
    });
    setCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await CategoryService.deleteCategory(categoryId);
      setCategories(categories.filter(cat => cat.id !== categoryId));
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleToggleCategoryStatus = async (categoryId: string) => {
    try {
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        const updatedCategory = await CategoryService.toggleCategoryStatus(categoryId, !category.is_active);
        setCategories(categories.map(cat => 
          cat.id === categoryId ? updatedCategory : cat
        ));
      }
    } catch (error) {
      console.error('Error toggling category status:', error);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) return;

    try {
      setIsUploadingImage(true);
      
      if (editingCategory) {
        // Update existing category
        const updatedCategory = await CategoryService.updateCategoryWithImage(
          editingCategory.id,
          {
            name: categoryForm.name,
            description: categoryForm.description,
            is_active: categoryForm.isActive,
            is_job_category: categoryForm.isJobCategory,
          },
          categoryForm.imageFile || undefined,
          categoryForm.imageFile ? undefined : categoryForm.imageUrl
        );
        setCategories(categories.map(cat => 
          cat.id === editingCategory.id ? updatedCategory : cat
        ));
      } else {
        // Create new category
        const newCategory = await CategoryService.createCategoryWithImage(
          {
            name: categoryForm.name,
            description: categoryForm.description,
            is_active: categoryForm.isActive,
            is_job_category: categoryForm.isJobCategory,
            product_count: 0,
          },
          categoryForm.imageFile || undefined
        );
        
        // If no file was uploaded but URL was provided, update with URL
        if (!categoryForm.imageFile && categoryForm.imageUrl) {
          const updatedCategory = await CategoryService.updateCategory(newCategory.id, {
            image_url: categoryForm.imageUrl,
          });
          setCategories([...categories, updatedCategory]);
        } else {
          setCategories([...categories, newCategory]);
        }
      }

      setCategoryModalOpen(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCategoryForm(prev => ({ 
        ...prev, 
        imageFile: file,
        imageUrl: '', // Clear URL when file is selected
      }));
    }
  };

  const handleImageUrlChange = (url: string) => {
    setCategoryForm(prev => ({ 
      ...prev, 
      imageUrl: url,
      imageFile: null, // Clear file when URL is entered
    }));
  };

  // Subcategory management functions
  const loadSubcategories = async () => {
    try {
      setSubcategoriesLoading(true);
      const fetchedSubcategories = await SubcategoryService.getSubcategories();
      setSubcategories(fetchedSubcategories);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  const handleCreateSubcategory = () => {
    setEditingSubcategory(null);
    setSubcategoryForm({
      name: '',
      description: '',
      categoryId: '',
      imageUrl: '',
      imageFile: null,
      isActive: true,
      isJobSubcategory: false,
    });
    setSubcategoryModalOpen(true);
  };

  const handleEditSubcategory = (subcategory: Subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      name: subcategory.name,
      description: subcategory.description || '',
      categoryId: subcategory.category_id,
      imageUrl: subcategory.image_url || '',
      imageFile: null,
      isActive: subcategory.is_active,
      isJobSubcategory: subcategory.is_job_subcategory,
    });
    setSubcategoryModalOpen(true);
  };

  const handleDeleteSubcategory = async (subcategoryId: string) => {
    try {
      await SubcategoryService.deleteSubcategoryWithImage(subcategoryId);
      setSubcategories(subcategories.filter(sub => sub.id !== subcategoryId));
    } catch (error) {
      console.error('Error deleting subcategory:', error);
    }
  };

  const handleToggleSubcategoryStatus = async (subcategoryId: string) => {
    try {
      const subcategory = subcategories.find(sub => sub.id === subcategoryId);
      if (subcategory) {
        const updatedSubcategory = await SubcategoryService.toggleSubcategoryStatus(subcategoryId, !subcategory.is_active);
        setSubcategories(subcategories.map(sub => 
          sub.id === subcategoryId ? updatedSubcategory : sub
        ));
      }
    } catch (error) {
      console.error('Error toggling subcategory status:', error);
    }
  };

  const handleSaveSubcategory = async () => {
    if (!subcategoryForm.name.trim() || !subcategoryForm.categoryId) return;

    try {
      setIsUploadingSubcategoryImage(true);
      
      if (editingSubcategory) {
        // Update existing subcategory
        const updatedSubcategory = await SubcategoryService.updateSubcategoryWithImage(
          editingSubcategory.id,
          {
            name: subcategoryForm.name,
            description: subcategoryForm.description,
            category_id: subcategoryForm.categoryId,
            is_active: subcategoryForm.isActive,
            is_job_subcategory: subcategoryForm.isJobSubcategory,
          },
          subcategoryForm.imageFile || undefined,
          subcategoryForm.imageFile ? undefined : subcategoryForm.imageUrl
        );
        setSubcategories(subcategories.map(sub => 
          sub.id === editingSubcategory.id ? updatedSubcategory : sub
        ));
      } else {
        // Create new subcategory
        const newSubcategory = await SubcategoryService.createSubcategoryWithImage(
          {
            name: subcategoryForm.name,
            description: subcategoryForm.description,
            category_id: subcategoryForm.categoryId,
            is_active: subcategoryForm.isActive,
            is_job_subcategory: subcategoryForm.isJobSubcategory,
            product_count: 0,
          },
          subcategoryForm.imageFile || undefined
        );
        
        // If no file was uploaded but URL was provided, update with URL
        if (!subcategoryForm.imageFile && subcategoryForm.imageUrl) {
          const updatedSubcategory = await SubcategoryService.updateSubcategory(newSubcategory.id, {
            image_url: subcategoryForm.imageUrl,
          });
          setSubcategories([...subcategories, updatedSubcategory]);
        } else {
          setSubcategories([...subcategories, newSubcategory]);
        }
      }

      setSubcategoryModalOpen(false);
      setEditingSubcategory(null);
    } catch (error) {
      console.error('Error saving subcategory:', error);
    } finally {
      setIsUploadingSubcategoryImage(false);
    }
  };

  const handleSubcategoryImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSubcategoryForm(prev => ({ ...prev, imageFile: file, imageUrl: '' }));
    }
  };

  const handleSubcategoryImageUrlChange = (url: string) => {
    setSubcategoryForm(prev => ({ ...prev, imageUrl: url, imageFile: null }));
  };

  // Subcategory filter functions
  const handleSubcategoryFilterChange = (filterType: string, value: any) => {
    setSubcategoryFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearSubcategoryFilters = () => {
    setSubcategoryFilters({
      search: '',
      category: '',
      status: 'all',
      sortBy: 'name',
      sortOrder: 'asc',
      subcategoryType: 'all',
    });
  };

  const getFilteredSubcategories = () => {
    let filtered = [...subcategories];

    // Search filter
    if (subcategoryFilters.search) {
      const searchTerm = subcategoryFilters.search.toLowerCase();
      filtered = filtered.filter(subcategory =>
        subcategory.name.toLowerCase().includes(searchTerm) ||
        subcategory.description?.toLowerCase().includes(searchTerm) ||
        categories.find(cat => cat.id === subcategory.category_id)?.name.toLowerCase().includes(searchTerm)
      );
    }

    // Category filter
    if (subcategoryFilters.category) {
      filtered = filtered.filter(subcategory => subcategory.category_id === subcategoryFilters.category);
    }

    // Status filter
    if (subcategoryFilters.status !== 'all') {
      const isActive = subcategoryFilters.status === 'active';
      filtered = filtered.filter(subcategory => subcategory.is_active === isActive);
    }

    // Subcategory type filter
    if (subcategoryFilters.subcategoryType !== 'all') {
      filtered = filtered.filter(subcategory => 
        subcategoryFilters.subcategoryType === 'products' ? !subcategory.is_job_subcategory : subcategory.is_job_subcategory
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (subcategoryFilters.sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at);
          bValue = new Date(b.updated_at);
          break;
        case 'product_count':
          aValue = a.product_count;
          bValue = b.product_count;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (subcategoryFilters.sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (subcategoryFilters.search) count++;
    if (subcategoryFilters.category) count++;
    if (subcategoryFilters.status !== 'all') count++;
    if (subcategoryFilters.subcategoryType !== 'all') count++;
    return count;
  };

  // Accordion functions
  const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    console.log(`Accordion ${section} changed to:`, isExpanded);
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded,
    }));
  };

  // Category filter functions
  const handleCategoryFilterChange = (filterType: string, value: any) => {
    setCategoryFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearCategoryFilters = () => {
    setCategoryFilters({
      search: '',
      status: 'all',
      sortBy: 'name',
      sortOrder: 'asc',
      categoryType: 'all',
    });
  };

  const getFilteredCategories = () => {
    let filtered = [...categories];

    // Search filter
    if (categoryFilters.search) {
      const searchTerm = categoryFilters.search.toLowerCase();
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchTerm) ||
        category.description?.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (categoryFilters.status !== 'all') {
      const isActive = categoryFilters.status === 'active';
      filtered = filtered.filter(category => category.is_active === isActive);
    }

    // Category type filter
    if (categoryFilters.categoryType === 'products') {
      filtered = filtered.filter(category => !category.is_job_category);
    } else if (categoryFilters.categoryType === 'jobs') {
      filtered = filtered.filter(category => category.is_job_category);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (categoryFilters.sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at);
          bValue = new Date(b.updated_at);
          break;
        case 'product_count':
          aValue = a.product_count;
          bValue = b.product_count;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (categoryFilters.sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  };

  const getActiveCategoryFilterCount = () => {
    let count = 0;
    if (categoryFilters.search) count++;
    if (categoryFilters.status !== 'all') count++;
    if (categoryFilters.categoryType !== 'all') count++;
    return count;
  };

  const renderDashboard = () => (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Dashboard Overview
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back! Here's what's happening with your platform.
      </Typography>

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 3,
        mb: 4 
      }}>
        <Box>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Users
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {mockStats.totalUsers.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUpIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                        +{mockStats.userGrowth}%
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <PeopleIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>

        <Box>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Products
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {mockStats.totalProducts.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUpIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                        +{mockStats.productGrowth}%
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                    <InventoryIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>

        <Box>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Orders
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {mockStats.totalOrders.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUpIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                        +{mockStats.orderGrowth}%
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                    <ShoppingCartIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>

        <Box>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Revenue
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      ${mockStats.totalRevenue.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TrendingUpIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                        +{mockStats.revenueGrowth}%
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                    <AnalyticsIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>
      </Box>

      {/* Recent Orders */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Recent Orders
            </Typography>
            <Button variant="outlined" size="small">
              View All
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockRecentOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Typography fontWeight="bold">{order.id}</Typography>
                    </TableCell>
                    <TableCell>{order.customer}</TableCell>
                    <TableCell>{order.product}</TableCell>
                    <TableCell>${order.amount}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={order.status}
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary">
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton size="small" color="secondary">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const renderUsers = () => {
    const getUserDisplayName = (user: User) => {
      if (user.alias) return user.alias;
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim();
      }
      return user.email.split('@')[0];
    };

    const getUserInitials = (user: User) => {
      if (user.alias) {
        return user.alias.substring(0, 2).toUpperCase();
      }
      if (user.first_name || user.last_name) {
        const first = user.first_name?.charAt(0) || '';
        const last = user.last_name?.charAt(0) || '';
        return (first + last).toUpperCase() || user.email.charAt(0).toUpperCase();
      }
      return user.email.charAt(0).toUpperCase();
    };

    const formatDate = (dateString: string | null) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              User Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage users, roles, and permissions ({users.length} total users)
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={loadUsers} disabled={usersLoading}>
            Refresh
          </Button>
        </Box>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              User tools
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Find by email, alias, or user id"
                value={userToolsQuery}
                onChange={(e) => setUserToolsQuery(e.target.value)}
                size="small"
                sx={{ minWidth: 320, flex: 1 }}
              />
              <Button
                variant="contained"
                onClick={runUserToolsSearch}
                disabled={usersLoading || userToolsBusy !== null}
                sx={{ textTransform: 'none' }}
              >
                Find
              </Button>
              <Button
                variant="outlined"
                onClick={clearUserToolsSearch}
                disabled={usersLoading || userToolsBusy !== null}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={findAndDeleteByQuery}
                disabled={usersLoading || userToolsBusy !== null}
                sx={{ textTransform: 'none' }}
              >
                Find & delete
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 6 }}>
                <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No users found
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status Preference</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Join Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: user.is_admin ? 'primary.main' : 'grey.500' }}>
                              {getUserInitials(user)}
                            </Avatar>
                            <Box>
                              <Typography fontWeight="medium">
                                {getUserDisplayName(user)}
                              </Typography>
                              {user.alias && (user.first_name || user.last_name) && (
                                <Typography variant="caption" color="text.secondary">
                                  {`${user.first_name || ''} ${user.last_name || ''}`.trim()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_admin ? 'Admin' : 'User'}
                            color={user.is_admin ? 'error' : 'default'}
                            size="small"
                            sx={{ fontWeight: user.is_admin ? 600 : 400 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.user_status_preference || 'consumer'}
                            color={user.user_status_preference === 'vendor' ? 'primary' : 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell>
                          <IconButton size="small" color="primary" onClick={() => openUserView(user)}>
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton size="small" color="primary" onClick={() => openUserEdit(user)}>
                            <EditIcon />
                          </IconButton>
                          {!user.is_admin && (
                            <IconButton size="small" color="error" onClick={() => openUserDelete(user)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Dialog open={userViewOpen} onClose={() => setUserViewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>User details</DialogTitle>
          <DialogContent dividers>
            {!selectedUser ? (
              <Typography color="text.secondary">No user selected.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField label="User ID" value={String(selectedUser.id)} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Email" value={String((selectedUser as any).email || '')} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Alias" value={String((selectedUserProfile?.alias ?? (selectedUser as any).alias) || '')} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Name" value={String(((selectedUserProfile?.first_name ?? (selectedUser as any).first_name) || '') + ' ' + ((selectedUserProfile?.last_name ?? (selectedUser as any).last_name) || '')).trim()} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Role" value={String((selectedUserProfile?.is_admin ?? (selectedUser as any).is_admin) ? 'admin' : 'user')} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Status preference" value={String((selectedUserProfile?.user_status_preference ?? (selectedUser as any).user_status_preference) || '')} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Created" value={String((selectedUserProfile?.created_at ?? (selectedUser as any).created_at) || '')} fullWidth InputProps={{ readOnly: true }} sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserViewOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={userEditOpen} onClose={() => setUserEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit user</DialogTitle>
          <DialogContent dividers>
            {!selectedUser ? (
              <Typography color="text.secondary">No user selected.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 0.5 }}>
                <TextField
                  label="Alias"
                  value={editUserForm.alias}
                  onChange={(e) => setEditUserForm((p) => ({ ...p, alias: e.target.value }))}
                  fullWidth
                  sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
                />
                <TextField
                  label="First name"
                  value={editUserForm.first_name}
                  onChange={(e) => setEditUserForm((p) => ({ ...p, first_name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Last name"
                  value={editUserForm.last_name}
                  onChange={(e) => setEditUserForm((p) => ({ ...p, last_name: e.target.value }))}
                  fullWidth
                />
                <FormControlLabel
                  control={<Switch checked={editUserForm.is_admin} onChange={(e) => setEditUserForm((p) => ({ ...p, is_admin: e.target.checked }))} />}
                  label="Admin"
                  sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
                />
                <Select
                  fullWidth
                  value={editUserForm.user_status_preference}
                  onChange={(e) => setEditUserForm((p) => ({ ...p, user_status_preference: String(e.target.value) }))}
                  sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
                >
                  <MenuItem value="consumer">Consumer</MenuItem>
                  <MenuItem value="vendor">Vendor</MenuItem>
                </Select>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserEditOpen(false)} disabled={userActionLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={saveUserEdits} variant="contained" disabled={userActionLoading} sx={{ textTransform: 'none' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={userDeleteOpen} onClose={() => setUserDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete user?</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary">
              This will remove the user from the <code>users</code> table. If hard delete is blocked, the system will attempt a safe “soft delete”.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <TextField label="Email" value={String((selectedUser as any)?.email || '')} fullWidth InputProps={{ readOnly: true }} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserDeleteOpen(false)} disabled={userActionLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button onClick={deleteUser} color="error" variant="contained" disabled={userActionLoading} sx={{ textTransform: 'none' }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderCategories = () => (
    <Box>
      <Accordion 
        expanded={expandedSections.categories} 
        onChange={handleAccordionChange('categories')}
        sx={{ mb: 2 }}
        square={false}
        elevation={1}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-expanded': { bgcolor: 'primary.dark' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <CategoryIcon sx={{ mr: 2 }} />
            <Typography variant="h5" fontWeight="bold">
              Categories {expandedSections.categories ? '(Expanded)' : '(Collapsed)'}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {categories.length} total
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateCategory();
                }}
                sx={{ 
                  textTransform: 'none',
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                Add Category
              </Button>
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {/* Category Filter Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowCategoryFilters(!showCategoryFilters)}
                  sx={{ textTransform: 'none' }}
                >
                  Filters
                  {getActiveCategoryFilterCount() > 0 && (
                    <Badge badgeContent={getActiveCategoryFilterCount()} color="primary" sx={{ ml: 1 }}>
                      <Box />
                    </Badge>
                  )}
                </Button>
                {getActiveCategoryFilterCount() > 0 && (
                  <Button
                    variant="text"
                    startIcon={<ClearIcon />}
                    onClick={clearCategoryFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Box>

            {/* Category Filter Panel */}
            <Collapse in={showCategoryFilters}>
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {/* Search */}
                  <TextField
                    fullWidth
                    label="Search categories"
                    value={categoryFilters.search}
                    onChange={(e) => handleCategoryFilterChange('search', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Search by name or description"
                  />

                  {/* Status Filter */}
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={categoryFilters.status}
                      onChange={(e) => handleCategoryFilterChange('status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="inactive">Inactive Only</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Sort By */}
                  <FormControl fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={categoryFilters.sortBy}
                      onChange={(e) => handleCategoryFilterChange('sortBy', e.target.value)}
                      label="Sort By"
                    >
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="created_at">Created Date</MenuItem>
                      <MenuItem value="updated_at">Updated Date</MenuItem>
                      <MenuItem value="product_count">Product Count</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Sort Order */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Sort Order:
                  </Typography>
                  <Button
                    variant={categoryFilters.sortOrder === 'asc' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleCategoryFilterChange('sortOrder', 'asc')}
                    startIcon={<SortIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Ascending
                  </Button>
                  <Button
                    variant={categoryFilters.sortOrder === 'desc' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleCategoryFilterChange('sortOrder', 'desc')}
                    startIcon={<SortIcon sx={{ transform: 'rotate(180deg)' }} />}
                    sx={{ textTransform: 'none' }}
                  >
                    Descending
                  </Button>
                </Box>

                {/* Category Type Selector */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                    Category Type
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* All Categories Option */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ flex: 1 }}
                    >
                      <Card
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: `2px solid ${categoryFilters.categoryType === 'all' ? '#4caf50' : 'transparent'}`,
                          bgcolor: categoryFilters.categoryType === 'all' ? '#e8f5e8' : 'background.paper',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#4caf50',
                            bgcolor: '#f1f8e9',
                          },
                        }}
                        onClick={() => handleCategoryFilterChange('categoryType', 'all')}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: categoryFilters.categoryType === 'all' ? '#4caf50' : '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <CategoryIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            All
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Both types
                          </Typography>
                        </Box>
                      </Card>
                    </motion.div>

                    {/* Product Categories Option */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ flex: 1 }}
                    >
                      <Card
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: `2px solid ${categoryFilters.categoryType === 'products' ? '#2196f3' : 'transparent'}`,
                          bgcolor: categoryFilters.categoryType === 'products' ? '#e3f2fd' : 'background.paper',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#2196f3',
                            bgcolor: '#f3f9ff',
                          },
                        }}
                        onClick={() => handleCategoryFilterChange('categoryType', 'products')}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: categoryFilters.categoryType === 'products' ? '#2196f3' : '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <ShoppingCartIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            Products
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Items only
                          </Typography>
                        </Box>
                      </Card>
                    </motion.div>

                    {/* Job Categories Option */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ flex: 1 }}
                    >
                      <Card
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: `2px solid ${categoryFilters.categoryType === 'jobs' ? '#ff9800' : 'transparent'}`,
                          bgcolor: categoryFilters.categoryType === 'jobs' ? '#fff3e0' : 'background.paper',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#ff9800',
                            bgcolor: '#fff8f0',
                          },
                        }}
                        onClick={() => handleCategoryFilterChange('categoryType', 'jobs')}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: categoryFilters.categoryType === 'jobs' ? '#ff9800' : '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <PeopleIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            Jobs
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Employment
                          </Typography>
                        </Box>
                      </Card>
                    </motion.div>
                  </Box>
                </Box>
              </Paper>
            </Collapse>

            {/* Results Summary */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {getFilteredCategories().length} of {categories.length} categories
              </Typography>
              {getActiveCategoryFilterCount() > 0 && (
                <Typography variant="body2" color="primary">
                  {getActiveCategoryFilterCount()} filter(s) applied
                </Typography>
              )}
            </Box>

            {/* Categories Grid */}
            {categoriesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : getFilteredCategories().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <FilterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No categories found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {getActiveCategoryFilterCount() > 0 
                    ? 'Try adjusting your filters or search terms'
                    : 'No categories have been created yet'
                  }
                </Typography>
                {getActiveCategoryFilterCount() > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearCategoryFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear All Filters
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 3,
                mb: 4
              }}>
                {getFilteredCategories().map((category) => (
          <motion.div
            key={category.id}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
              {/* Category Image */}
              <Box sx={{ position: 'relative', height: 200 }}>
                <img
                  src={category.image_url || 'https://via.placeholder.com/400x200?text=No+Image'}
                  alt={category.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  gap: 1,
                }}>
                  <Chip
                    label={category.is_active ? 'Active' : 'Inactive'}
                    color={category.is_active ? 'success' : 'default'}
                    size="small"
                    sx={{ 
                      fontWeight: 'bold',
                      bgcolor: category.is_active ? '#4caf50' : '#f5f5f5',
                      color: category.is_active ? 'white' : '#666',
                      '& .MuiChip-label': {
                        color: category.is_active ? 'white' : '#666',
                      }
                    }}
                  />
                </Box>
              </Box>

              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {category.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                  {category.description || 'No description available'}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {category.product_count} products
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Updated: {new Date(category.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditCategory(category)}
                    sx={{ textTransform: 'none', flex: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color={category.is_active ? 'warning' : 'success'}
                    onClick={() => handleToggleCategoryStatus(category.id)}
                    sx={{ textTransform: 'none' }}
                  >
                    {category.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
                ))}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Subcategories Section */}
      <Accordion 
        expanded={expandedSections.subcategories} 
        onChange={handleAccordionChange('subcategories')}
        sx={{ mb: 2 }}
        square={false}
        elevation={1}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: 'secondary.main', 
            color: 'white',
            '&:hover': { bgcolor: 'secondary.dark' },
            '&.Mui-expanded': { bgcolor: 'secondary.dark' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <SubcategoryIcon sx={{ mr: 2 }} />
            <Typography variant="h5" fontWeight="bold">
              Subcategories {expandedSections.subcategories ? '(Expanded)' : '(Collapsed)'}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {subcategories.length} total
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateSubcategory();
                }}
                sx={{ 
                  textTransform: 'none',
                  bgcolor: 'white',
                  color: 'secondary.main',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                Add Subcategory
              </Button>
            </Box>
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {/* Subcategory Filter Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowSubcategoryFilters(!showSubcategoryFilters)}
                  sx={{ textTransform: 'none' }}
                >
                  Filters
                  {getActiveFilterCount() > 0 && (
                    <Badge badgeContent={getActiveFilterCount()} color="primary" sx={{ ml: 1 }}>
                      <Box />
                    </Badge>
                  )}
                </Button>
                {getActiveFilterCount() > 0 && (
                  <Button
                    variant="text"
                    startIcon={<ClearIcon />}
                    onClick={clearSubcategoryFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Box>

          {/* Filter Panel */}
          <Collapse in={showSubcategoryFilters}>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                {/* Search */}
                <TextField
                  fullWidth
                  label="Search subcategories"
                  value={subcategoryFilters.search}
                  onChange={(e) => handleSubcategoryFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Search by name, description, or category"
                />

                {/* Category Filter */}
                <FormControl fullWidth>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={subcategoryFilters.category}
                    onChange={(e) => handleSubcategoryFilterChange('category', e.target.value)}
                    label="Filter by Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Status Filter */}
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={subcategoryFilters.status}
                    onChange={(e) => handleSubcategoryFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="inactive">Inactive Only</MenuItem>
                  </Select>
                </FormControl>

                {/* Subcategory Type Filter */}
                <FormControl fullWidth>
                  <InputLabel>Subcategory Type</InputLabel>
                  <Select
                    value={subcategoryFilters.subcategoryType}
                    onChange={(e) => handleSubcategoryFilterChange('subcategoryType', e.target.value)}
                    label="Subcategory Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="products">Product Subcategories</MenuItem>
                    <MenuItem value="jobs">Job Subcategories</MenuItem>
                  </Select>
                </FormControl>

                {/* Sort By */}
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={subcategoryFilters.sortBy}
                    onChange={(e) => handleSubcategoryFilterChange('sortBy', e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="created_at">Created Date</MenuItem>
                    <MenuItem value="updated_at">Updated Date</MenuItem>
                    <MenuItem value="product_count">Product Count</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Sort Order */}
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Sort Order:
                </Typography>
                <Button
                  variant={subcategoryFilters.sortOrder === 'asc' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleSubcategoryFilterChange('sortOrder', 'asc')}
                  startIcon={<SortIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Ascending
                </Button>
                <Button
                  variant={subcategoryFilters.sortOrder === 'desc' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleSubcategoryFilterChange('sortOrder', 'desc')}
                  startIcon={<SortIcon sx={{ transform: 'rotate(180deg)' }} />}
                  sx={{ textTransform: 'none' }}
                >
                  Descending
                </Button>
              </Box>
            </Paper>
          </Collapse>

          {/* Results Summary */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {getFilteredSubcategories().length} of {subcategories.length} subcategories
            </Typography>
            {getActiveFilterCount() > 0 && (
              <Typography variant="body2" color="primary">
                {getActiveFilterCount()} filter(s) applied
              </Typography>
            )}
          </Box>
          
          {subcategoriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : getFilteredSubcategories().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <FilterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No subcategories found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {getActiveFilterCount() > 0 
                  ? 'Try adjusting your filters or search terms'
                  : 'No subcategories have been created yet'
                }
              </Typography>
              {getActiveFilterCount() > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearSubcategoryFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 4
            }}>
              {getFilteredSubcategories().map((subcategory) => {
                const parentCategory = categories.find(cat => cat.id === subcategory.category_id);
                return (
                  <motion.div
                    key={subcategory.id}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                      {/* Subcategory Image */}
                      <Box sx={{ position: 'relative', height: 150 }}>
                        <img
                          src={subcategory.image_url || 'https://via.placeholder.com/400x150?text=No+Image'}
                          alt={subcategory.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          gap: 1,
                        }}>
                          <Chip
                            label={subcategory.is_active ? 'Active' : 'Inactive'}
                            color={subcategory.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ 
                              fontWeight: 'bold',
                              bgcolor: subcategory.is_active ? '#4caf50' : '#f5f5f5',
                              color: subcategory.is_active ? 'white' : '#666',
                              '& .MuiChip-label': {
                                color: subcategory.is_active ? 'white' : '#666',
                              }
                            }}
                          />
                        </Box>
                      </Box>

                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {subcategory.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Category:</strong> {parentCategory?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {subcategory.description || 'No description available'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {subcategory.product_count} products
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Updated: {new Date(subcategory.updated_at).toLocaleDateString()}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditSubcategory(subcategory)}
                            sx={{ textTransform: 'none', flex: 1 }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color={subcategory.is_active ? 'warning' : 'success'}
                            onClick={() => handleToggleSubcategoryStatus(subcategory.id)}
                            sx={{ textTransform: 'none' }}
                          >
                            {subcategory.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSubcategory(subcategory.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </Box>
          )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Category Form Modal */}
      <Dialog
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Create New Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 3 }}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />

            {/* Category Type Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                Category Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Product Category Option */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ flex: 1 }}
                >
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: `2px solid ${!categoryForm.isJobCategory ? theme.palette.primary.main : 'transparent'}`,
                      bgcolor: !categoryForm.isJobCategory ? theme.palette.primary.light + '20' : 'background.paper',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: theme.palette.primary.light + '10',
                      },
                    }}
                    onClick={() => setCategoryForm(prev => ({ ...prev, isJobCategory: false }))}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: !categoryForm.isJobCategory ? theme.palette.primary.main : theme.palette.grey[300],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <ShoppingCartIcon sx={{ color: 'white', fontSize: 30 }} />
                      </Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                        Product Category
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        For regular products and items
                      </Typography>
                      {!categoryForm.isJobCategory && (
                        <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                      )}
                    </Box>
                  </Card>
                </motion.div>

                {/* Job Category Option */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ flex: 1 }}
                >
                  <Card
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      border: `2px solid ${categoryForm.isJobCategory ? theme.palette.secondary.main : 'transparent'}`,
                      bgcolor: categoryForm.isJobCategory ? theme.palette.secondary.light + '20' : 'background.paper',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: theme.palette.secondary.main,
                        bgcolor: theme.palette.secondary.light + '10',
                      },
                    }}
                    onClick={() => setCategoryForm(prev => ({ ...prev, isJobCategory: true }))}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: categoryForm.isJobCategory ? theme.palette.secondary.main : theme.palette.grey[300],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <PeopleIcon sx={{ color: 'white', fontSize: 30 }} />
                      </Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                        Job Category
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        For job postings and employment
                      </Typography>
                      {categoryForm.isJobCategory && (
                        <CheckCircleIcon sx={{ color: theme.palette.secondary.main, fontSize: 24 }} />
                      )}
                    </Box>
                  </Card>
                </motion.div>
              </Box>
            </Box>

            {/* Image Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Category Image
              </Typography>
              
              {/* URL Input */}
              <TextField
                fullWidth
                label="Image URL"
                value={categoryForm.imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                placeholder="https://example.com/image.jpg"
                disabled={isUploadingImage}
                sx={{ mb: 2 }}
              />
              
              {/* File Upload */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none' }}
                  disabled={isUploadingImage}
                >
                  {isUploadingImage ? 'Uploading...' : 'Upload File'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploadingImage}
                  />
                </Button>
                {categoryForm.imageFile && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {categoryForm.imageFile.name}
                  </Typography>
                )}
              </Box>
              
              {/* Image Preview */}
              {(categoryForm.imageFile || categoryForm.imageUrl) && (
                <Box sx={{ mt: 2, position: 'relative' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Preview:
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={categoryForm.imageFile ? URL.createObjectURL(categoryForm.imageFile) : categoryForm.imageUrl}
                      alt="Preview"
                      style={{
                        width: 200,
                        height: 150,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '1px solid #ddd',
                        opacity: isUploadingImage ? 0.6 : 1,
                      }}
                    />
                    {isUploadingImage && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: 8,
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={40} />
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            Uploading...
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={categoryForm.isActive}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            disabled={!categoryForm.name.trim() || isUploadingImage}
            startIcon={isUploadingImage ? <CircularProgress size={20} /> : undefined}
          >
            {isUploadingImage 
              ? (categoryForm.imageFile ? 'Uploading Image...' : 'Saving...') 
              : (editingCategory ? 'Update' : 'Create')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Form Modal */}
      <Dialog
        open={subcategoryModalOpen}
        onClose={() => setSubcategoryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSubcategory ? 'Edit Subcategory' : 'Create New Subcategory'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Subcategory Name"
              value={subcategoryForm.name}
              onChange={(e) => setSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 3 }}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={subcategoryForm.description}
              onChange={(e) => setSubcategoryForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />

            {/* Subcategory Type Selector */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Subcategory Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ flex: 1 }}
                >
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: subcategoryForm.isJobSubcategory ? 'none' : '2px solid',
                      borderColor: subcategoryForm.isJobSubcategory ? 'transparent' : 'primary.main',
                      bgcolor: subcategoryForm.isJobSubcategory ? 'grey.100' : 'primary.50',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.100',
                      }
                    }}
                    onClick={() => setSubcategoryForm(prev => ({ ...prev, isJobSubcategory: false }))}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ShoppingCartIcon sx={{ color: subcategoryForm.isJobSubcategory ? 'text.secondary' : 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: subcategoryForm.isJobSubcategory ? 'text.secondary' : 'primary.main' }}>
                        Product Subcategory
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      For organizing products within categories
                    </Typography>
                  </Card>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ flex: 1 }}
                >
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: subcategoryForm.isJobSubcategory ? '2px solid' : 'none',
                      borderColor: subcategoryForm.isJobSubcategory ? 'secondary.main' : 'transparent',
                      bgcolor: subcategoryForm.isJobSubcategory ? 'secondary.50' : 'grey.100',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'secondary.main',
                        bgcolor: 'secondary.100',
                      }
                    }}
                    onClick={() => setSubcategoryForm(prev => ({ ...prev, isJobSubcategory: true }))}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PeopleIcon sx={{ color: subcategoryForm.isJobSubcategory ? 'secondary.main' : 'text.secondary' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: subcategoryForm.isJobSubcategory ? 'secondary.main' : 'text.secondary' }}>
                        Job Subcategory
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      For organizing job postings within categories
                    </Typography>
                  </Card>
                </motion.div>
              </Box>
            </Box>

            {/* Category Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Parent Category *
              </Typography>
              <TextField
                fullWidth
                select
                value={subcategoryForm.categoryId}
                onChange={(e) => setSubcategoryForm(prev => ({ ...prev, categoryId: e.target.value }))}
                SelectProps={{
                  native: true,
                }}
                required
                helperText={
                  subcategoryForm.isJobSubcategory 
                    ? "Only job categories are shown" 
                    : "Only product categories are shown"
                }
              >
                <option value="">Select a category</option>
                {categories
                  .filter(category => 
                    subcategoryForm.isJobSubcategory 
                      ? category.is_job_category 
                      : !category.is_job_category
                  )
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
              </TextField>
            </Box>

            {/* Image Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Subcategory Image
              </Typography>
              
              {/* URL Input */}
              <TextField
                fullWidth
                label="Image URL"
                value={subcategoryForm.imageUrl}
                onChange={(e) => handleSubcategoryImageUrlChange(e.target.value)}
                placeholder="https://example.com/image.jpg"
                disabled={isUploadingSubcategoryImage}
                sx={{ mb: 2 }}
              />
              
              {/* File Upload */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none' }}
                  disabled={isUploadingSubcategoryImage}
                >
                  {isUploadingSubcategoryImage ? 'Uploading...' : 'Upload File'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleSubcategoryImageUpload}
                    disabled={isUploadingSubcategoryImage}
                  />
                </Button>
                {subcategoryForm.imageFile && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {subcategoryForm.imageFile.name}
                  </Typography>
                )}
              </Box>
              
              {/* Image Preview */}
              {(subcategoryForm.imageFile || subcategoryForm.imageUrl) && (
                <Box sx={{ mt: 2, position: 'relative' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Preview:
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={subcategoryForm.imageFile ? URL.createObjectURL(subcategoryForm.imageFile) : subcategoryForm.imageUrl}
                      alt="Preview"
                      style={{
                        width: 200,
                        height: 150,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '2px solid #e0e0e0',
                      }}
                    />
                    {isUploadingSubcategoryImage && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          borderRadius: 8,
                        }}
                      >
                        <CircularProgress size={24} sx={{ mb: 1 }} />
                        <Typography variant="caption">Uploading...</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Active Status */}
            <FormControlLabel
              control={
                <Switch
                  checked={subcategoryForm.isActive}
                  onChange={(e) => setSubcategoryForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active (visible to users)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubcategoryModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSubcategory}
            disabled={!subcategoryForm.name.trim() || !subcategoryForm.categoryId || isUploadingSubcategoryImage}
            startIcon={isUploadingSubcategoryImage ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {isUploadingSubcategoryImage ? 'Uploading...' : editingSubcategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderProducts = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {is_job ? 'Job Management' : 'Product Management'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {is_job ? 'Manage job postings and their locations' : 'Manage products and their locations'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}>
          {is_job ? 'Add Job' : 'Add Product'}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Price/Salary</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Coordinates</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Mock data for demonstration */}
                {is_job ? (
                  <>
                    <TableRow>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, width: 40, height: 40 }}>
                            <WorkIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Senior React Developer
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              TechCorp Inc.
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label="Job" color="primary" size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          $120,000/year
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          San Francisco, CA
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          37.7749°N, 122.4194°W
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label="Active" color="success" size="small" />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, width: 40, height: 40 }}>
                            <WorkIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Marketing Manager
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              StartupXYZ
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label="Job" color="primary" size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          $85,000/year
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Remote
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          No coordinates
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label="Active" color="success" size="small" />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, width: 40, height: 40 }}>
                          <StoreIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Premium Cotton T-Shirt
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            FashionHub
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label="Product" color="secondary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        $49.99
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        New York, NY
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        40.7128°N, 74.0060°W
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label="Active" color="success" size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const renderSidebar = () => (
    (() => {
      const expandedWidth = 280;
      const collapsedWidth = 76;
      const drawerWidth = !isMobile && sidebarCollapsed ? collapsedWidth : expandedWidth;
      const isCollapsed = !isMobile && sidebarCollapsed;

      return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: 'grey.50',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shortest,
          }),
        },
      }}
    >
      <Box sx={{ p: isCollapsed ? 1.5 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isCollapsed ? 1.5 : 3 }}>
          {isCollapsed ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Tooltip title="Expand sidebar" placement="right">
                <IconButton
                  onClick={toggleSidebarCollapsed}
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 10px 30px rgba(2, 132, 199, 0.12)',
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                  size="small"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                Admin Panel
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!isMobile && (
                  <Tooltip title="Collapse sidebar">
                    <IconButton
                      onClick={toggleSidebarCollapsed}
                      sx={{
                        bgcolor: 'white',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 30px rgba(2, 132, 199, 0.12)',
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                      size="small"
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isMobile && (
                  <IconButton onClick={() => setSidebarOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Box>
        <List>
          {menuItems.map((item) => {
            if (item.id !== 'mail' && item.id !== 'platform-purchases' && item.id !== 'kundencenter') {
              const listItem = (
                <ListItem
                  key={item.id}
                  onClick={() => handleMenuClick(item.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    cursor: 'pointer',
                    bgcolor: activeTab === item.id ? 'primary.main' : 'transparent',
                    color: activeTab === item.id ? 'white' : 'text.primary',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    px: isCollapsed ? 1 : undefined,
                    '&:hover': {
                      bgcolor: activeTab === item.id ? 'primary.dark' : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: activeTab === item.id ? 'white' : 'text.secondary',
                      minWidth: isCollapsed ? 'auto' : undefined,
                      mr: isCollapsed ? 0 : undefined,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={item.label} />}
                </ListItem>
              );

              return isCollapsed ? (
                <Tooltip key={item.id} title={item.label} placement="right" arrow>
                  {listItem}
                </Tooltip>
              ) : (
                listItem
              );
            }

            if (item.id === 'kundencenter') {
              if (isCollapsed) {
                const listItem = (
                  <ListItem
                    key={item.id}
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: 'pointer',
                      bgcolor: isKundencenterActive ? 'primary.main' : 'transparent',
                      color: isKundencenterActive ? 'white' : 'text.primary',
                      justifyContent: 'center',
                      px: 1,
                      '&:hover': {
                        bgcolor: isKundencenterActive ? 'primary.dark' : 'grey.100',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isKundencenterActive ? 'white' : 'text.secondary',
                        minWidth: 'auto',
                        mr: 0,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  </ListItem>
                );

                return (
                  <Tooltip key={item.id} title={item.label} placement="right" arrow>
                    {listItem}
                  </Tooltip>
                );
              }

              return (
                <Box key={item.id} sx={{ mb: 0.5 }}>
                  <ListItem
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isKundencenterActive ? 'primary.main' : 'transparent',
                      color: isKundencenterActive ? 'white' : 'text.primary',
                      '&:hover': {
                        bgcolor: isKundencenterActive ? 'primary.dark' : 'grey.100',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: isKundencenterActive ? 'white' : 'text.secondary' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                    <Tooltip title={kundencenterMenuOpen ? 'Collapse' : 'Expand'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleKundencenterMenu();
                        }}
                        sx={{ color: isKundencenterActive ? 'white' : 'text.secondary' }}
                      >
                        {kundencenterMenuOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </ListItem>

                  <Collapse in={kundencenterMenuOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 2, pt: 0.5 }}>
                      <ListItemButton
                        onClick={() => openKundencenterReports()}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          bgcolor: isKundencenterActive ? 'grey.100' : 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            bgcolor: 'grey.100',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                          <SubcategoryIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Reports" />
                      </ListItemButton>
                    </Box>
                  </Collapse>
                </Box>
              );
            }

            if (item.id === 'platform-purchases') {
              if (isCollapsed) {
                const listItem = (
                  <ListItem
                    key={item.id}
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: 'pointer',
                      bgcolor: isPlatformPurchasesActive ? 'primary.main' : 'transparent',
                      color: isPlatformPurchasesActive ? 'white' : 'text.primary',
                      justifyContent: 'center',
                      px: 1,
                      '&:hover': {
                        bgcolor: isPlatformPurchasesActive ? 'primary.dark' : 'grey.100',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isPlatformPurchasesActive ? 'white' : 'text.secondary',
                        minWidth: 'auto',
                        mr: 0,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  </ListItem>
                );

                return (
                  <Tooltip key={item.id} title={item.label} placement="right" arrow>
                    {listItem}
                  </Tooltip>
                );
              }

              return (
                <Box key={item.id} sx={{ mb: 0.5 }}>
                  <ListItem
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isPlatformPurchasesActive ? 'primary.main' : 'transparent',
                      color: isPlatformPurchasesActive ? 'white' : 'text.primary',
                      '&:hover': {
                        bgcolor: isPlatformPurchasesActive ? 'primary.dark' : 'grey.100',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: isPlatformPurchasesActive ? 'white' : 'text.secondary' }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                    <Tooltip title={platformPurchasesMenuOpen ? 'Collapse' : 'Expand'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlatformPurchasesMenu();
                        }}
                        sx={{ color: isPlatformPurchasesActive ? 'white' : 'text.secondary' }}
                      >
                        {platformPurchasesMenuOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </ListItem>

                  <Collapse in={platformPurchasesMenuOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 2, pt: 0.5 }}>
                      {[
                        { id: 'platform-purchases-boost', label: 'Boost & Visibility', icon: <TrendingUpIcon fontSize="small" /> },
                        { id: 'platform-purchases-subscriptions', label: 'Platform Subscriptions', icon: <CreditCardIcon fontSize="small" /> },
                      ].map((sub) => {
                        const active = activeTab === sub.id;
                        return (
                          <ListItemButton
                            key={sub.id}
                            onClick={() => openPlatformPurchasesSubTab(sub.id as any)}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              bgcolor: active ? 'grey.100' : 'transparent',
                              color: 'text.primary',
                              '&:hover': {
                                bgcolor: 'grey.100',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{sub.icon}</ListItemIcon>
                            <ListItemText primary={sub.label} />
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            }

            if (isCollapsed) {
              const listItem = (
                <ListItem
                  key={item.id}
                  onClick={() => handleMenuClick(item.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    cursor: 'pointer',
                    bgcolor: isMailActive ? 'primary.main' : 'transparent',
                    color: isMailActive ? 'white' : 'text.primary',
                    justifyContent: 'center',
                    px: 1,
                    '&:hover': {
                      bgcolor: isMailActive ? 'primary.dark' : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isMailActive ? 'white' : 'text.secondary',
                      minWidth: 'auto',
                      mr: 0,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </ListItem>
              );

              return (
                <Tooltip key={item.id} title={item.label} placement="right" arrow>
                  {listItem}
                </Tooltip>
              );
            }

            return (
              <Box key={item.id} sx={{ mb: 0.5 }}>
                <ListItem
                  onClick={() => handleMenuClick(item.id)}
                  sx={{
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: isMailActive ? 'primary.main' : 'transparent',
                    color: isMailActive ? 'white' : 'text.primary',
                    '&:hover': {
                      bgcolor: isMailActive ? 'primary.dark' : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isMailActive ? 'white' : 'text.secondary' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  <Tooltip title={mailMenuOpen ? 'Collapse' : 'Expand'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMailMenu();
                      }}
                      sx={{ color: isMailActive ? 'white' : 'text.secondary' }}
                    >
                      {mailMenuOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </ListItem>

                <Collapse in={mailMenuOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 2, pt: 0.5 }}>
                    {[ 
                      { id: 'mail-subscribers', label: 'Subscribers' },
                      { id: 'mail-groups', label: 'Groups' },
                      { id: 'mail-campaigns', label: 'Campaigns' },
                      { id: 'mail-notifications', label: 'Notifications' },
                    ].map((sub) => {
                      const active = activeTab === sub.id;
                      return (
                        <ListItemButton
                          key={sub.id}
                          onClick={() => openMailSubTab(sub.id as any)}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            bgcolor: active ? 'grey.100' : 'transparent',
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'grey.100',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                            <SubcategoryIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={sub.label} />
                        </ListItemButton>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
          
          {/* Logout Option */}
          {(isCollapsed ? (
            <Tooltip title="Logout" placement="right" arrow>
              <ListItem
                onClick={() => handleMenuClick('logout')}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  color: 'error.main',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  px: isCollapsed ? 1 : undefined,
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'white',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'error.main', minWidth: isCollapsed ? 'auto' : undefined, mr: isCollapsed ? 0 : undefined, justifyContent: 'center' }}>
                  <LogoutIcon />
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary="Logout" />}
              </ListItem>
            </Tooltip>
          ) : (
            <ListItem
              onClick={() => handleMenuClick('logout')}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                cursor: 'pointer',
                bgcolor: 'transparent',
                color: 'error.main',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1 : undefined,
                '&:hover': {
                  bgcolor: 'error.light',
                  color: 'white',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'error.main', minWidth: isCollapsed ? 'auto' : undefined, mr: isCollapsed ? 0 : undefined, justifyContent: 'center' }}>
                <LogoutIcon />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary="Logout" />}
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
      );
    })()
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'users':
        return renderUsers();
      case 'kundencenter':
        return <KundencenterTab />;
      case 'categories':
        return renderCategories();
      case 'products':
        return renderProducts();
      case 'transactions':
        return <TransactionsTab />;
      case 'platform-purchases':
      case 'platform-purchases-boost':
        return <BoostVisibilityTab />;
      case 'platform-purchases-subscriptions':
        return <PlatformSubscriptionsTab />;
      case 'mail':
        return <MailProvidersTab />;
      case 'mail-subscribers':
        return <PlatformSubscribersTab />;
      case 'mail-groups':
        return <PlatformGroupsTab />;
      case 'mail-campaigns':
        return <PlatformCampaignsTab />;
      case 'mail-notifications':
        return <PlatformNotificationsTab />;
      case 'mailjet':
        // legacy id support
        return <MailProvidersTab />;
      
      case 'homepage':
        return <HomepageSettingsTab />;
      case 'website-settings':
        return <WebsiteSettingsTab />;
      case 'integrations':
        return <IntegrationsApiKeysTab />;
      case 'chatbot':
        return <ChatbotTab />;
      case 'orders':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Order Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage customer orders
            </Typography>
          </Box>
        );
      case 'analytics':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Analytics & Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View detailed analytics and generate reports
            </Typography>
          </Box>
        );
      case 'notifications':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Notifications
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage system notifications and alerts
            </Typography>
          </Box>
        );
      case 'security':
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Security Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure security settings and access controls
            </Typography>
          </Box>
        );
      case 'settings':
        // Settings now opens as a modal, so this case should not be reached
        // But keeping it as fallback
        return (
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              System Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Click the Settings menu item to open settings
            </Typography>
          </Box>
        );
      default:
        return renderDashboard();
    }
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verifying admin access...
        </Typography>
      </Box>
    );
  }

  // Redirect if not admin
  if (!isAdmin) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {renderSidebar()}
      
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top Bar */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton onClick={() => setSidebarOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
            {!isMobile && (
              <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} arrow>
                <IconButton
                  onClick={toggleSidebarCollapsed}
                  sx={{
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                  size="small"
                >
                  {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="h6" fontWeight="bold">
              {menuItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Welcome, {displayName}
              </Typography>
              <Tooltip title="Admin menu" arrow>
                <IconButton
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={{ p: 0.5 }}
                >
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    {avatarLetter}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <AdminUserMenu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          displayName={displayName}
          avatarLetter={avatarLetter}
          onLogout={handleLogout}
          onBackToSite={() => navigate('/')}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        isAdmin={true}
      />

      {/* Product Categories Modal (same as mobile Kategorien) */}
      <Dialog
        open={productCategoriesOpen}
        onClose={() => setProductCategoriesOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            width: isMobile ? '100vw' : { xs: '92vw', sm: 680, md: 860 },
            mx: 'auto',
            maxHeight: isMobile ? '100vh' : '80vh',
            overflow: 'hidden',
          },
        }}
      >
        <AppBar sx={{ position: 'sticky', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
          <Toolbar sx={{ minHeight: 48 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Kategorien
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveCategoriesEnabled}
              disabled={savingCategories}
              sx={{ mr: 1, textTransform: 'none' }}
            >
              {savingCategories ? 'Saving…' : 'Save'}
            </Button>
            <IconButton edge="end" onClick={() => setProductCategoriesOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 1, overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 48px)' : 'calc(80vh - 48px)' }}>
          {loadingSettings && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading settings…</Typography>
            </Box>
          )}
          {Object.keys(defaultMegaMenuData)
            .filter((k) => k.toLowerCase() !== 'default')
            .map((cat) => {
              const catData = (defaultMegaMenuData as any)[cat];
              if (!catData) return null;
              return (
                <Accordion key={cat} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75, display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 700, flex: 1 }}>{cat}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!categoriesEnabled[cat]}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleCategoryEnabled(cat);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={categoriesEnabled[cat] ? 'Enabled' : 'Disabled'}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 1 }}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                    {catData.left.map((item: any) => (
                      <Accordion key={`${cat}-${item.key}`} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5, display: 'flex', alignItems: 'center' }}>
                          {item.icon && <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>}
                          <Typography sx={{ fontWeight: 600, flex: 1 }}>{item.label}</Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={isSubcategoryGroupEnabled(cat, item.key)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleSubcategoryGroup(cat, item.key, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label={isSubcategoryGroupEnabled(cat, item.key) ? 'Enabled' : 'Disabled'}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ mr: 0.5 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                          {(catData.rightByKey?.[item.key] || []).map((col: any) => (
                            <Box key={`${cat}-${item.key}-${col.title}`} sx={{ mb: 1.25 }}>
                              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>
                                {col.title}
                              </Typography>
                              <List disablePadding>
                                {(col.items || []).map((sub: any) => {
                                  const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                  if (!label) return null;
                                  const checked = !!(subcategoriesEnabled?.[cat]?.[label]);
                                  return (
                                    <ListItem key={`${cat}-${item.key}-${col.title}-${label}`} sx={{ py: 0.25 }}
                                      secondaryAction={
                                        <FormControlLabel
                                          control={
                                            <Switch
                                              size="small"
                                              checked={checked}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleToggleSubcategoryEnabled(cat, label);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          }
                                          label={checked ? 'Enabled' : 'Disabled'}
                                          onClick={(e) => e.stopPropagation()}
                                          sx={{ mr: 0.5 }}
                                        />
                                      }
                                    >
                                      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </Box>
      </Dialog>

      {/* Job Categories Modal (mirrors job mega menu/navigation categories) */}
      <Dialog
        open={jobCategoriesOpen}
        onClose={() => setJobCategoriesOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            width: isMobile ? '100vw' : { xs: '92vw', sm: 680, md: 860 },
            mx: 'auto',
            maxHeight: isMobile ? '100vh' : '80vh',
            overflow: 'hidden',
          },
        }}
      >
        <AppBar sx={{ position: 'sticky', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
          <Toolbar sx={{ minHeight: 48 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Job Categories
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveCategoriesEnabled}
              disabled={savingCategories}
              sx={{ mr: 1, textTransform: 'none' }}
            >
              {savingCategories ? 'Saving…' : 'Save'}
            </Button>
            <IconButton edge="end" onClick={() => setJobCategoriesOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 1, overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 48px)' : 'calc(80vh - 48px)' }}>
          {loadingSettings && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading settings…</Typography>
            </Box>
          )}
          {Object.keys(jobMegaMenuData)
            .filter((k) => k.toLowerCase() !== 'default')
            .map((cat) => {
              const catData: any = (jobMegaMenuData as any)[cat];
              if (!catData) return null;

              const roleCount = (() => {
                const set = new Set<string>();
                const r = catData?.rightByKey || {};
                Object.keys(r).forEach((k: string) => {
                  (r[k] || []).forEach((col: any) => {
                    (col.items || []).forEach((it: any) => {
                      const label = typeof it === 'string' ? it : it?.label ?? '';
                      if (label) set.add(String(label));
                    });
                  });
                });
                return set.size;
              })();

              return (
                <Accordion key={cat} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75, display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 700, flex: 1 }}>{cat}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!categoriesEnabled[cat]}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleCategoryEnabled(cat);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={categoriesEnabled[cat] ? 'Enabled' : 'Disabled'}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 1 }}
                    />
                    <Chip size="small" label={`${roleCount} roles`} sx={{ mr: 1 }} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                    {(catData.left || []).map((item: any) => (
                      <Accordion
                        key={`${cat}-${item.key}`}
                        disableGutters
                        elevation={0}
                        sx={{ '&::before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5, display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ fontWeight: 600, flex: 1 }}>{item.label}</Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={isJobSubcategoryGroupEnabled(cat, item.key)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleJobSubcategoryGroup(cat, item.key, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label={isJobSubcategoryGroupEnabled(cat, item.key) ? 'Enabled' : 'Disabled'}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ mr: 0.5 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                          {((catData.rightByKey?.[item.key] || catData.rightByKey?.all) || []).map((col: any) => (
                            <Box key={`${cat}-${item.key}-${col.title}`} sx={{ mb: 1.25 }}>
                              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>
                                {col.title}
                              </Typography>
                              <List dense disablePadding>
                                {(col.items || []).map((sub: any) => {
                                  const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                  if (!label) return null;
                                  const checked = !!(subcategoriesEnabled?.[cat]?.[label]);
                                  return (
                                    <ListItem
                                      key={`${cat}-${item.key}-${col.title}-${label}`}
                                      sx={{ py: 0.25 }}
                                      secondaryAction={
                                        <FormControlLabel
                                          control={
                                            <Switch
                                              size="small"
                                              checked={checked}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleToggleSubcategoryEnabled(cat, label);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          }
                                          label={checked ? 'Enabled' : 'Disabled'}
                                          onClick={(e) => e.stopPropagation()}
                                          sx={{ mr: 0.5 }}
                                        />
                                      }
                                    >
                                      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
